import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  useBoolean,
  useToast,
} from "@chakra-ui/react";
import {
  useApiError,
  withApiError,
} from "@raiden/library/components/ReactHookForm/ApiError";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import { USERS_USER_TYPE_VALUE_CUSTOMER } from "@raiden/library/constants/users";
import useAuthAs from "@raiden/library/hooks/auth/as";
import useApiFetcher from "@raiden/library/hooks/useApiFetcher";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { authLoginSerialize } from "@raiden/library/normalizers/auth";
import NextLink from "next/link";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import { FormattedMessage, useIntl } from "react-intl";
import ApiErrorMessage from "../../../components/ApiErrorMessage";
import useRequest from "@raiden/library/hooks/useRequest";
import ChallengeApp from "./ChallengeApp";
import ChallengeEmail from "./ChallengeEmail";
import ChallengeSms from "./ChallengeSms";

/**
 * @typedef {"standard" | "2-factor" | "2-factor-recovery"} AuthenticationType
 *
 * @typedef {"app" | "sms" | "email"} AuthenticatorMethod
 *
 * @typedef {object} AuthenticatorChallenge
 * @property {string} challenge_id
 * @property {string} method
 * @property {string} [email]
 * @property {string} [phone_number]
 *
 * @typedef {{}} Props
 * @param {import("react").PropsWithChildren<Props>} props
 */
function SignInForm(props) {
  const [isSuccess, setIsSuccess] = useState(false);

  /** @type {import("react").MutableRefObject<HTMLElement | null> } */
  const codeInputRef = useRef(null);
  /** @type {import("react").MutableRefObject<HTMLElement | null> } */
  const verificationCodeInputRef = useRef(null);

  const intl = useIntl();

  const [isPasswordVisible, { toggle: togglePasswordVisibility }] =
    useBoolean(false);

  const [authenticationType, setAuthenticationType] = useState(
    /** @type {AuthenticationType} */ ("standard"),
  );

  const [challenge, setChallenge] = useState(
    /** @type {AuthenticatorChallenge | null} **/ (null),
  );

  const [isLoading, setIsLoading] = useState(false);

  const [isVerificationCodeSent, setIsVerificationCodeSent] = useState(false);

  const { request } = useRequest();

  const apiFetcher = useApiFetcher();

  const toast = useToast();

  const { login, user } = useAuthAs();

  const { resolveApiError, resetApiErrors } = useApiError();

  const form = useForm({
    defaultValues: {
      data: {
        username: "",
        password: "",
        remember: false,
        code: "", // 2-factor
        recovery_code: "", // 2-factor-recovery
        user_type: USERS_USER_TYPE_VALUE_CUSTOMER,
      },
    },
  });
  const { handleSubmit, setValue } = form;

  async function onSubmit(fields) {
    setIsLoading(true);

    if (user?.user_type && USERS_USER_TYPE_VALUE_CUSTOMER !== user.user_type) {
      await apiFetcher(
        generateApiUri({
          id: "@auth.logout",
        }),
        {
          method: "POST",
        },
      ).catch(() => {
        toast({
          status: "error",
          title: intl.formatMessage({
            defaultMessage:
              "Une erreur est survenue lors de la tentative de déconnexion.",
          }),
        });
      });
    }

    request(
      generateApiUri({
        id: "@auth.login",
      }),
      {
        method: "POST",
        body: authLoginSerialize({ authenticationType, payload: fields }),
      },
    )
      .then((response) => {
        resetApiErrors();
        if (response?.data?.two_factor_authentication_challenge_required) {
          setAuthenticationType("2-factor");
          setChallenge(response.data);
        } else {
          setIsSuccess(true);
          toast({
            id: "loginSuccess",
            title: intl.formatMessage({
              defaultMessage: "Connexion réussie !",
            }),
            status: "success",
            duration: 5000,
            position: "bottom-right",
            isClosable: true,
          });
          login(response?.data);
        }
      })
      .catch((response) => {
        resolveApiError({ response, form });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    if (authenticationType === "standard") {
      setValue("data.code", "");
      setValue("data.recovery_code", "");
    }
    resetApiErrors();
  }, [authenticationType, resetApiErrors, setValue]);

  useEffect(() => {
    setIsVerificationCodeSent(challenge?.method === "app" ? true : false);
  }, [challenge]);

  // auto focus code input
  useEffect(() => {
    if (isVerificationCodeSent) {
      codeInputRef.current?.focus();
    }
  }, [isVerificationCodeSent]);

  // auto focus verification code input
  useEffect(() => {
    if (authenticationType === "2-factor-recovery") {
      verificationCodeInputRef.current?.focus();
    }
  }, [authenticationType]);

  return (
    <>
      <Box w="full">
        <ApiErrorMessage />

        <FormProvider {...form}>
          <form action="#" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing="1rem">
              {authenticationType === "standard" && (
                <>
                  <FormController
                    name="data.username"
                    label={intl.formatMessage({
                      defaultMessage: "Votre identifiant de connexion",
                    })}
                    placeholder={intl.formatMessage({
                      defaultMessage: "dupont@example.fr",
                    })}
                    rules={{ required: true }}
                  />

                  <FormController
                    name="data.password"
                    rules={{ required: true }}
                    render={(field) => (
                      <FormControl
                        isInvalid={field.isInvalid}
                        isRequired={field.isRequired}>
                        <FormLabel>
                          <FormattedMessage defaultMessage="Votre mot de passe" />
                        </FormLabel>

                        <InputGroup>
                          <Input
                            {...field}
                            type={isPasswordVisible ? "text" : "password"}
                            placeholder="••••••••••"
                          />

                          <InputRightElement>
                            <IconButton
                              onClick={togglePasswordVisibility}
                              icon={
                                <Icon
                                  as={
                                    isPasswordVisible
                                      ? IoEyeOffOutline
                                      : IoEyeOutline
                                  }
                                />
                              }
                              aria-label={intl.formatMessage({
                                defaultMessage:
                                  "Afficher/masquer le mot de passe",
                              })}
                              size="sm"
                            />
                          </InputRightElement>
                        </InputGroup>
                      </FormControl>
                    )}
                  />

                  <FormController
                    name="data.remember"
                    render={(field) => (
                      <FormControl id="remember" isInvalid={field.isInvalid}>
                        <Checkbox {...field}>
                          {intl.formatMessage({
                            defaultMessage: "Se souvenir de moi ?",
                          })}
                        </Checkbox>
                      </FormControl>
                    )}
                  />
                </>
              )}

              {authenticationType === "2-factor" && (
                <>
                  {challenge?.method === "app" && <ChallengeApp />}

                  {challenge?.method === "sms" && (
                    <ChallengeSms
                      challenge={challenge}
                      setIsVerificationCodeSent={setIsVerificationCodeSent}
                    />
                  )}

                  {challenge?.method === "email" && (
                    <ChallengeEmail
                      challenge={challenge}
                      setIsVerificationCodeSent={setIsVerificationCodeSent}
                    />
                  )}

                  <FormController
                    ref={codeInputRef}
                    name="data.code"
                    label={intl.formatMessage({
                      defaultMessage: "Code de vérification",
                    })}
                    placeholder="XXXXXX"
                    rules={{
                      required: true,
                      onChange: (event) => {
                        // @ts-ignore
                        if (event.target.value.length === 6) {
                          handleSubmit(onSubmit)();
                        }
                      },
                    }}
                    isDisabled={!isVerificationCodeSent}
                  />
                </>
              )}

              {authenticationType === "2-factor-recovery" && (
                <FormController
                  ref={verificationCodeInputRef}
                  name="data.recovery_code"
                  label={intl.formatMessage({
                    defaultMessage: "Code de récupération",
                  })}
                  placeholder="XXXXXXXXXX-XXXXXXXXXX"
                  rules={{
                    required: true,
                    onChange: (event) => {
                      // @ts-ignore
                      if (event.target.value.length === 21) {
                        handleSubmit(onSubmit)();
                      }
                    },
                  }}
                />
              )}

              <Button
                type="submit"
                isLoading={isLoading}
                isDisabled={
                  isSuccess ||
                  (!isVerificationCodeSent && authenticationType === "2-factor")
                }
                colorScheme="brandSecondary">
                {(() => {
                  switch (authenticationType) {
                    case "2-factor":
                      return intl.formatMessage({
                        defaultMessage: "Vérifier le code",
                      });
                    case "2-factor-recovery":
                      return intl.formatMessage({
                        defaultMessage: "Récupérer l'accès",
                      });
                    default:
                      return "Se connecter";
                  }
                })()}
              </Button>

              {authenticationType === "standard" && (
                <Box textAlign="center">
                  <NextLink
                    href={generateFrontPath({ id: "@password-recovery" })}
                    passHref={true}
                    legacyBehavior>
                    <Link color="blue.500">
                      {intl.formatMessage({
                        defaultMessage: "Mot de passe oublié ?",
                      })}
                    </Link>
                  </NextLink>
                </Box>
              )}

              {authenticationType === "2-factor" && (
                <Button
                  onClick={() => {
                    setAuthenticationType("2-factor-recovery");
                  }}
                  variant="outline"
                  size="sm">
                  <FormattedMessage defaultMessage="Se connecter avec un code de récupération" />
                </Button>
              )}

              {["2-factor", "2-factor-recovery"].includes(
                authenticationType,
              ) && (
                <Button
                  onClick={() => {
                    setAuthenticationType("standard");
                  }}
                  variant="outline"
                  colorScheme="gray"
                  size="sm">
                  <FormattedMessage defaultMessage="Retour" />
                </Button>
              )}
            </Stack>
          </form>
        </FormProvider>
      </Box>
    </>
  );
}

export default withApiError(SignInForm);
